import "../styles/dashboard/dashboard.css";
import { useNavigate, useParams } from "react-router-dom";
import ProfileAvatar from "./users/UserProfile";
import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import Header from "./Header";
import { fetchUploadedFiles } from "./documents/DocsManagement";
import DashboardSidebar from "./DashboardSideBar";
import { useTranslation } from 'react-i18next';
import { useAuth } from "./context/AuthContext";
import Loading from "./Loading";
import { Roles } from "../roles";

const tusApi = process.env.REACT_APP_TUS_SERVER;

export function formatMillionUp(value, decimals = 2) {
    if (value == null || Number.isNaN(Number(value))) return { number: '-', unit: '' };

    const n = Number(value);

    if (Math.abs(n) >= 1_000_000_000) {
        return { number: (n / 1_000_000_000).toFixed(decimals), unit: 'Billion' };
    }

    if (Math.abs(n) >= 1_000_000) {
        return { number: (n / 1_000_000).toFixed(decimals), unit: 'Million' };
    }

    // Even small numbers return object now
    return { number: n.toLocaleString(), unit: '' };
}


export default function Dashboard() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { lang } = useParams();
    const [users, setUsers] = useState([]);
    const { user, role, token, loading } = useAuth();
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const username = user?.userName || "";
    const apiUrl = process.env.REACT_APP_OPT_SERVER;
    const [summary, setSummary] = useState({
        totalUsers: 0,
        totalFiles: 0,
        totalSize: { value: 0, unit: 'B' },
        totalUniqueEntities: 0
    });
    const [showMenu, setShowMenu] = useState(false);

    const toggleMenu = () => {
        setShowMenu(!showMenu);
    };
    const [stats, setStats] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const AVERAGE_KHMER_WORD_LENGTH = 6.9;   // average characters per word
    const AVERAGE_KHMER_TOKEN_LENGTH = 4.3;  // average characters per token
    const TEXT_RATIO = 0.6;                  // estimated ratio of text in files (HTML/DOCX/TXT)
    const isClickable = role === 0 || role === 1;
    const navigateDocManagement = (username) => {
        if (role === Roles.ADMIN || role === Roles.SUPERADMIN) {
            navigate(`/lang/${lang}/admin/doc-manager`, {
                state: { viewedByAdmin: true },
            });
        }
    };


    const navigateUserManagement = (username) => {
        if (role === Roles.ADMIN || role === Roles.SUPERADMIN) {
            navigate(`/lang/${lang}/admin/user-manager`, {
                state: { viewedByAdmin: true },
            });
        }
    };

    const totalSizeBytes = uploadedFiles
        .filter(file => file.completed)  // only include completed files
        .reduce((sum, file) => sum + file.fileSize, 0);

    const totalSizeMB = totalSizeBytes / (1024 * 1024);

    useEffect(() => {
        if (!username || !token) return;

        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch files first
                const files = await fetchUploadedFiles(tusApi, username, token);
                setUploadedFiles(files);

                // Decode username from token
                const decoded = jwtDecode(token);
                const usernameFromToken = decoded?.username;
                if (!usernameFromToken) throw new Error("Username not found in token");

                // Fetch users with fetch()
                const usersRes = await fetch(
                    `${tusApi}/api/uploads/admin-dashboard?userId=${encodeURIComponent(usernameFromToken)}`,
                    {
                        method: "GET",
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json"
                        }
                    }
                );

                if (!usersRes.ok) {
                    throw new Error(`HTTP error! status: ${usersRes.status}`);
                }

                const { users: fetchedUsers = [], stats = [] } = await usersRes.json();

                // Merge user data with stats
                const mergedUsers = fetchedUsers.map(user => {
                    const stat = stats.find(s => s.username === user.username) || {};
                    return {
                        ...user,
                        fileCount: stat.fileCount || 0,
                        totalSize: stat.totalSize || 0,
                        totalSizeMB: stat.totalSizeMB || "0.00",
                    };
                });

                setUsers(mergedUsers);

                const formatFileSize = (bytes) => {
                    if (!bytes) return { value: 0, unit: 'B' };

                    if (bytes < 1024) return { value: bytes, unit: 'B' };
                    if (bytes < 1024 * 1024) return { value: +(bytes / 1024).toFixed(1), unit: 'KB' };
                    if (bytes < 1024 * 1024 * 1024) return { value: +(bytes / (1024 * 1024)).toFixed(1), unit: 'MB' };

                    return { value: +(bytes / (1024 * 1024 * 1024)).toFixed(2), unit: 'GB' };
                };

                // Calculate summary
                const totalUsers = mergedUsers.length;
                const totalFiles = mergedUsers.reduce((sum, user) => sum + user.fileCount, 0);
                const totalSizeBytes = mergedUsers.reduce((sum, user) => sum + user.totalSize, 0);

                // Format number & unit
                const totalSize = formatFileSize(totalSizeBytes);
                const uniqueEntities = [...new Set(mergedUsers.map((user) => user.entity))];

                // Count how many users per entity
                // const entityCounts = mergedUsers.reduce((acc, user) => {
                //     acc[user.entity] = (acc[user.entity] || 0) + 1;
                //     return acc;
                // }, {});

                const totalUniqueEntities = uniqueEntities.length;

                setSummary({ totalUsers, totalFiles, totalSize, totalUniqueEntities });
            } catch (err) {
                console.error("Failed to load data:", err.message || err);
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [username, token, apiUrl]);

    const formatBytes = (bytes) => {
        if (bytes === 0) return { value: 0, unit: 'B' };

        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
        const unit = sizes[i];

        return { value, unit };
    };


    const loadFiles = async () => {
        try {
            const res = await fetch(`${tusApi}/api/uploads/admin-stat`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Failed to fetch uploaded files');

            const files = await res.json();

            // Group by createdAt date
            const grouped = files.reduce((acc, file) => {
                const dateObj = new Date(file.fileDate);
                if (isNaN(dateObj)) return acc;
                const date = dateObj.toLocaleDateString();

                if (!acc[date]) acc[date] = { files: [], totalSize: 0 };
                acc[date].files.push(file);
                acc[date].totalSize += file.fileSize || 0;
                return acc;
            }, {});

            // Convert grouped object into array and calculate status per date
            const statsArray = Object.entries(grouped).map(([date, data]) => {
                const allCompleted = data.files.every(
                    f => f.completed === true && !f.quarantine && !f.infected
                );

                return {
                    date,
                    totalFiles: data.files.length,
                    size: formatBytes(data.totalSize),
                    status: allCompleted ? 'done' : 'pending', // or 'Incomplete'
                };
            });

            setStats(statsArray);
        } catch (error) {
            console.error('Error loading files:', error.message);
        }
    };
    useEffect(() => {
        loadFiles();
    }, [username, token]);

    // const khmerMonths = [
    //     "មករា",
    //     "កុម្ភៈ",
    //     "មិនា",
    //     "មេសា",
    //     "ឧសភា",
    //     "មិថុនា",
    //     "កក្កដា",
    //     "សីហា",
    //     "កញ្ញា",
    //     "តុលា",
    //     "វិច្ឆិកា",
    //     "ធ្នូ",
    // ];

    // const formatDateKhmer = (date) => {
    //     const d = new Date(date);
    //     const day = d.getDate();
    //     const month = khmerMonths[d.getMonth()];
    //     const year = d.getFullYear();

    //     let hours = d.getHours();
    //     const minutes = d.getMinutes().toString().padStart(2, "0");
    //     const ampm = hours >= 12 ? "ល្ងាច" : "ព្រឹក";

    //     hours = hours % 12;
    //     if (hours === 0) hours = 12; // convert 0 to 12 for 12-hour format
    //     const hoursStr = hours.toString().padStart(2, "0");

    //     return `${day} ${month} ${year}, ${hoursStr}:${minutes} ${ampm}`;
    // };
    if (loading || loadingData) return <Loading />;

    function sizeObjectToBytes(sizeObj) {
        if (!sizeObj || !sizeObj.unit) return 0;

        const units = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
        return (sizeObj.value || 0) * (units[sizeObj.unit] || 1);
    }

    const totalBytes = sizeObjectToBytes(summary.totalSize) * 0.15;
    const totalWords = Math.round((totalBytes * TEXT_RATIO) / AVERAGE_KHMER_WORD_LENGTH);
    const totalTokens = Math.round((totalBytes * TEXT_RATIO) / AVERAGE_KHMER_TOKEN_LENGTH);

    // const totalWords = Math.round((parseSizeToBytes(summary.totalSize?.value) * TEXT_RATIO) / AVERAGE_KHMER_WORD_LENGTH);
    // const totalTokens = Math.round((parseSizeToBytes(summary.totalSize?.value) * TEXT_RATIO) / AVERAGE_KHMER_TOKEN_LENGTH);
    const formatted_totalTokens = formatMillionUp(totalTokens);
    const formatted_totalWords = formatMillionUp(totalWords);
    return (

        <div className="uploadpage">
            <Header hideHamburger={false} profileHide={true} hideNav={true} toggleMenu={toggleMenu} />
            <div className="dashboard">
                <DashboardSidebar
                    uploadedFiles={uploadedFiles}
                    totalSizeMB={totalSizeMB}
                    showMenu={showMenu}
                    setShowMenu={setShowMenu}
                />
                <div div className="main-dashboard">
                    <div className="container">
                        <div className="head">
                            <h2>{t('dashboard')}</h2>
                        </div>
                        <div className="contents">
                            <div className="content">
                                <div className="totally">
                                    <div className="overview-container">

                                        <div className="overview">
                                            <div className={`total ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateDocManagement() : undefined}
                                            >
                                                <div className="userTotal">
                                                    <p>{t('total-storage')}</p>
                                                    <div className="total-color all-size">
                                                        <h3>{summary.totalSize?.value}</h3>
                                                        {summary.totalSize?.unit && <h4>{summary.totalSize?.unit}</h4>}
                                                    </div>

                                                </div>
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M160-280h640v-240H160v240Zm520-60q25 0 42.5-17.5T740-400q0-25-17.5-42.5T680-460q-25 0-42.5 17.5T620-400q0 25 17.5 42.5T680-340Zm200-260H767l-80-80H273l-80 80H80l137-137q11-11 25.5-17t30.5-6h414q16 0 30.5 6t25.5 17l137 137ZM160-200q-33 0-56.5-23.5T80-280v-320h800v320q0 33-23.5 56.5T800-200H160Z" /></svg>

                                            </div>
                                            <div className={`total ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateDocManagement() : undefined}
                                            >

                                                <div className="userTotal">
                                                    <p>{t('document count')}</p>
                                                    <div className="total-color all-files">
                                                        <h3>{summary.totalFiles} </h3>
                                                        <h4>{t('files')}</h4>
                                                    </div>

                                                </div>
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M120-120q-33 0-56.5-23.5T40-200v-480q0-17 11.5-28.5T80-720q17 0 28.5 11.5T120-680v480h640q17 0 28.5 11.5T800-160q0 17-11.5 28.5T760-120H120Zm160-160q-33 0-56.5-23.5T200-360v-440q0-33 23.5-56.5T280-880h167q16 0 30.5 6t25.5 17l57 57h280q33 0 56.5 23.5T920-720v360q0 33-23.5 56.5T840-280H280Zm0-80h560v-360H527l-80-80H280v440Zm0 0v-440 440Z" /></svg>

                                            </div>
                                        </div>


                                        <div className="overview">
                                            <div className="total not-cursor">
                                                <div className="userTotal">
                                                    <p>{t('total-words')}</p>
                                                    <div className="total-color all-words">
                                                        <h3>{formatted_totalWords.number}</h3>
                                                        {formatted_totalWords.unit && <h4>{formatted_totalWords.unit}</h4>}
                                                    </div>

                                                </div>

                                            </div>

                                            <div className="total not-cursor">
                                                <div className="userTotal">
                                                    <p>{t('total-tokens')}</p>
                                                    <div className="total-color all-tokens">
                                                        <h3>{formatted_totalTokens.number}</h3>
                                                        {formatted_totalTokens.unit && <h4>{formatted_totalTokens.unit}</h4>}
                                                    </div>

                                                </div>

                                            </div>

                                        </div>
                                    </div>

                                    <div className="user-overview">
                                        <h2>{t('user-file-overview')}</h2>
                                        <div className="total">
                                            <div className={`user-contain  ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateUserManagement() : undefined}>
                                                <div className="userTotal">
                                                    <p>{t('total-user')}</p>
                                                    <div className="total-color all-users">
                                                        <h3> {summary.totalUsers}</h3>
                                                        <h4> {t('users')}</h4>
                                                    </div>
                                                </div>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><g fill="none" fill-rule="evenodd"><path d="m12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036q-.016-.004-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z" /><path fill="currentColor" d="M12 12c1.873 0 3.57.62 4.815 1.487c1.183.825 2.185 2.051 2.185 3.37c0 .724-.309 1.324-.796 1.77c-.458.421-1.056.694-1.672.88C15.301 19.88 13.68 20 12 20s-3.301-.12-4.532-.493c-.616-.186-1.214-.459-1.673-.88C5.31 18.182 5 17.582 5 16.858c0-1.319 1.002-2.545 2.185-3.37C8.43 12.62 10.127 12 12 12m0 2c-1.44 0-2.743.48-3.67 1.127c-.989.69-1.33 1.392-1.33 1.73c0 .304.352.494.672.614l.205.07l.17.052c.94.284 2.32.407 3.953.407c1.508 0 2.799-.105 3.728-.344l.304-.087l.19-.06c.343-.117.778-.314.778-.652s-.341-1.04-1.33-1.73C14.744 14.481 13.44 14 12 14m7-1c1.044 0 1.992.345 2.693.833c.64.447 1.307 1.19 1.307 2.096c0 1.335-1.297 1.813-2.463 1.98l-.3.037l-.289.025l-.138.008c.122-.345.19-.72.19-1.122a3.8 3.8 0 0 0-.107-.888c.386-.03.703-.08.939-.151c.104-.032.01-.13-.1-.215l-.107-.078l-.076-.051a2.7 2.7 0 0 0-.995-.418c-.38-.76-.964-1.418-1.586-1.943A4.8 4.8 0 0 1 19 13M5 13q.537.002 1.032.113c-.622.525-1.206 1.183-1.586 1.943a2.7 2.7 0 0 0-.995.418l-.128.088c-.127.092-.276.22-.155.256c.236.071.553.122.94.151a3.7 3.7 0 0 0-.108.888c0 .402.068.777.19 1.122l-.28-.02l-.296-.03c-1.202-.147-2.614-.607-2.614-2c0-.905.666-1.649 1.307-2.096A4.76 4.76 0 0 1 5 13m13.5-6a2.5 2.5 0 1 1 0 5a2.5 2.5 0 0 1 0-5m-13 0a2.5 2.5 0 1 1 0 5a2.5 2.5 0 0 1 0-5M12 3a4 4 0 1 1 0 8a4 4 0 0 1 0-8m6.5 6a.5.5 0 1 0 0 1a.5.5 0 0 0 0-1m-13 0a.5.5 0 1 0 0 1a.5.5 0 0 0 0-1M12 5a2 2 0 1 0 0 4a2 2 0 0 0 0-4" /></g></svg>
                                            </div>
                                            <div className={`user-contain  ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateUserManagement() : undefined}>
                                                <div className="userTotal">
                                                    <p>{t('total-entity')}</p>
                                                    <div className="total-color">
                                                        <h3> {summary.totalUniqueEntities}</h3>
                                                        <h4> {t('entity')}</h4>
                                                    </div>
                                                </div>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linejoin="round" d="M14 3.5h-4c-3.771 0-5.657 0-6.828 1.172S2 7.729 2 11.5v1c0 3.771 0 5.657 1.172 6.828S6.229 20.5 10 20.5h4c3.771 0 5.657 0 6.828-1.172S22 16.271 22 12.5v-1c0-3.771 0-5.657-1.172-6.828S17.771 3.5 14 3.5Z" /><path stroke-linecap="round" d="M5 16c1.036-2.581 4.896-2.75 6 0" /><path d="M9.75 9.75a1.75 1.75 0 1 1-3.5 0a1.75 1.75 0 0 1 3.5 0Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M14 8.5h5M14 12h5m-5 3.5h2.5" /></g></svg>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                            </div>

                            <div className="recently">
                                <div className="rec-head">
                                    <h2>{t('contribute-history')}</h2>
                                </div>

                                {/* <div className="all-user" onClick={() =>
                                    handleDetails(user.username)
                                }>
                                    {users.slice(0, 10).map((user) => (
                                        <div className="user-card" key={user.id}>
                                            <div className="profile-avatar">
                                                <ProfileAvatar user={user} />
                                                <div className="userMailDate">
                                                    <span className="email">{user.email}</span>
                                                    <span className="created">
                                                        {i18n.language === "km"
                                                            ? formatDateKhmer(user.createdAt)
                                                            : new Date(user.createdAt).toLocaleString(i18n.language, {
                                                                year: "numeric",
                                                                month: "long",
                                                                day: "numeric",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            })}
                                                    </span>
                                                </div>
                                    </div>


                                    <div className="view">
                                        <button type="button" onClick={() =>
                                            handleDetails(user.username)
                                        }>
                                            {t('view')}
                                        </button>
                                    </div>

                                     </div>
                                    ))} */}
                                <div className="stats-board">
                                    <table>
                                        {/* Table Header */}
                                        <thead>
                                            <tr>
                                                <th>{t('date')}</th>
                                                <th>{t('files')}</th>
                                                <th>{t('file-size')}</th>
                                                <th>{t('status')}</th>
                                            </tr>
                                        </thead>

                                        {/* Table Body */}
                                        <tbody>
                                            {stats.length > 0 ? (
                                                stats.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.date}</td>
                                                        <td>{item.totalFiles} {t('files')}</td>
                                                        <td>
                                                            {item.size.value + ' ' + item.size.unit}

                                                        </td>
                                                        <td>
                                                            <div className={`status ${item.status}`}>
                                                                {t(item.status)}
                                                            </div></td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" style={{ textAlign: 'center', color: '#888', padding: '12px 0' }}>
                                                        {t('no-file-found')}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>


                            </div>
                        </div>
                    </div>
                </div>
            </div></div >

    );
}